import React from 'react';
import { styled } from '@compiled/react';
import { MenuGroup, Section, ButtonItem } from '@atlaskit/menu';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { borderRadius, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	DROPDOWN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useFireDateFilterSelectedAnalytics } from '../../../common/utils/analytics';
import { useDateFilter, RelativeDateFilterOptions } from '../../../controllers/date-filter';
import messages from './messages';

type Props = {
	onOpen: () => void;
	onClose: () => void;
};

const relativeOptions = [
	{
		value: RelativeDateFilterOptions.PAST_TWO_WEEKS,
		label: messages.pastTwoWeeksLabel,
		keyboardShortcut: 'w',
	},
	{
		value: RelativeDateFilterOptions.PAST_MONTH,
		label: messages.pastMonthLabel,
		keyboardShortcut: 'm',
	},
	{
		value: RelativeDateFilterOptions.PAST_QUARTER,
		label: messages.pastQuarterLabel,
		keyboardShortcut: 'q',
	},
	{
		value: RelativeDateFilterOptions.PAST_YEAR,
		label: messages.pastYearLabel,
		keyboardShortcut: 'y',
	},
];

const useFireDateFilterMenuItemClickedAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return (dateFilterType: string) =>
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'dateFilterMenuItem',
			{ dateFilterType },
		);
};

const PopupContent = ({ onOpen, onClose }: Props) => {
	const { formatMessage } = useIntl();
	const [, { setRelativeDateFilter }] = useDateFilter();
	const fireDateFilterMenuItemClickedAnalytics = useFireDateFilterMenuItemClickedAnalytics();
	const fireDateFilterSelectedAnalytics = useFireDateFilterSelectedAnalytics();

	return (
		<ContextualAnalyticsData sourceName="viewDateFilterMenu" sourceType={DROPDOWN}>
			<FireScreenAnalytics />
			<MenuGroup>
				<Section title={formatMessage(messages.dateFilterTitle)}>
					{relativeOptions.map((option) => (
						<ButtonItem
							testId={`software-deployments.ui.date-filter.popup-content.button-item-${option.value.toLowerCase()}`}
							key={option.value}
							onClick={() => {
								onClose();
								setRelativeDateFilter(option.value);
								fireDateFilterMenuItemClickedAnalytics(option.value.toLowerCase());
								fireDateFilterSelectedAnalytics(option.value.toLowerCase());
							}}
						>
							<ButtonItemContent>
								{formatMessage(option.label)}
								<ShortcutIcon>{option.keyboardShortcut}</ShortcutIcon>
							</ButtonItemContent>
						</ButtonItem>
					))}
				</Section>
				<Section hasSeparator>
					<ButtonItem
						testId="software-deployments.ui.date-filter.popup-content.button-item-custom-date"
						onClick={() => {
							onClose();
							onOpen();
							fireDateFilterMenuItemClickedAnalytics('custom-date');
						}}
					>
						{formatMessage(messages.customRangeLabel)}
					</ButtonItem>
				</Section>
			</MenuGroup>
		</ContextualAnalyticsData>
	);
};

export default PopupContent;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonItemContent = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 25.5}px`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShortcutIcon = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 2.5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2.5}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral', colors.N20),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon', colors.N200),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
});
